import { SUBSCRIPTION_ACKNOWLEDGMENT_TEXT } from '@perdiem/trpc/legal';
import type { RCPackage } from './purchases';

type SubscriptionPackagePresentation = {
  label: string;
  priceLabel: string;
  comparisonLabel: string | null;
  description: string;
  badge?: string;
};

export function orderSubscriptionPackagesForPaywall(packages: RCPackage[]): RCPackage[] {
  if (!packages.some((pkg) => pkg.freeTrial !== null)) return packages;

  const rank = (pkg: RCPackage) => {
    if (pkg.packageType === 'MONTHLY') return 0;
    if (pkg.packageType === 'ANNUAL') return 1;
    return 2;
  };

  return packages
    .map((pkg, index) => ({ pkg, index }))
    .sort((a, b) => rank(a.pkg) - rank(b.pkg) || a.index - b.index)
    .map(({ pkg }) => pkg);
}

export function getDefaultSubscriptionPackageId(packages: RCPackage[]): string | null {
  const preferredType = packages.some((pkg) => pkg.freeTrial !== null)
    ? 'MONTHLY'
    : 'ANNUAL';

  return packages.find((pkg) => pkg.packageType === preferredType)?.id
    ?? packages[0]?.id
    ?? null;
}

export function formatFreeTrialDuration(pkg: RCPackage): string | null {
  if (!pkg.freeTrial) return null;
  const { duration, unit } = pkg.freeTrial;
  return `${duration} ${unit}${duration === 1 ? '' : 's'}`;
}

function getFreeTrialEndDate(pkg: RCPackage, startsAt: Date): Date | null {
  if (!pkg.freeTrial) return null;

  const endsAt = new Date(startsAt);
  const { duration, unit } = pkg.freeTrial;
  switch (unit) {
    case 'day':
      endsAt.setDate(endsAt.getDate() + duration);
      break;
    case 'week':
      endsAt.setDate(endsAt.getDate() + duration * 7);
      break;
    case 'month':
      endsAt.setMonth(endsAt.getMonth() + duration);
      break;
    case 'year':
      endsAt.setFullYear(endsAt.getFullYear() + duration);
      break;
  }
  return endsAt;
}

export function formatFreeTrialEndDate(
  pkg: RCPackage,
  startsAt = new Date(),
): string | null {
  const endsAt = getFreeTrialEndDate(pkg, startsAt);
  if (!endsAt) return null;

  return endsAt.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    ...(endsAt.getFullYear() !== startsAt.getFullYear()
      ? { year: 'numeric' as const }
      : {}),
  });
}

export function getFreeTrialPaywallCopy(
  pkg: RCPackage,
  paymentMethodLabel = 'Payment method',
) {
  const durationLabel = formatFreeTrialDuration(pkg);
  const endDateLabel = formatFreeTrialEndDate(pkg);
  if (!durationLabel || !endDateLabel) return null;

  return {
    durationLabel,
    endDateLabel,
    badgeLabel: `${durationLabel} free · $0 today`,
    heading: `Try Per Diem free for ${durationLabel}`,
    description: `Pay $0 today. Cancel before ${endDateLabel} and you'll pay nothing.`,
    paymentMethodNote: `${paymentMethodLabel} required to start. You won't be charged today.`,
    acknowledgment: `My ${durationLabel} free trial starts today. On ${endDateLabel}, my selected plan starts at ${describePostTrialPricing(pkg)} unless I cancel first. ${SUBSCRIPTION_ACKNOWLEDGMENT_TEXT}`,
    ctaLabel: `Start my ${durationLabel} free trial`,
    reassurance: `No charge today · Cancel before ${endDateLabel} to pay $0`,
  };
}

export function getSubscriptionPeriodLabel(packageType: string): string {
  switch (packageType) {
    case 'MONTHLY': return 'month';
    case 'ANNUAL': return 'year';
    case 'WEEKLY': return 'week';
    default: return 'billing period';
  }
}

/** "$1.99/month for 2 months", or null when the package has no intro phase. */
export function formatIntroPriceOffer(pkg: RCPackage): string | null {
  if (!pkg.introPrice) return null;
  const { priceString, periodUnit, totalDuration } = pkg.introPrice;
  return `${priceString}/${periodUnit} for ${totalDuration} ${periodUnit}${totalDuration === 1 ? '' : 's'}`;
}

/**
 * The full post-trial pricing ladder, e.g.
 * "$1.99/month for 2 months, then $8.99/month" — or just "$8.99/month"
 * when there is no introductory phase. Callers prefix "Then " / "After that, "
 * as their sentence requires.
 */
export function describePostTrialPricing(pkg: RCPackage): string {
  const ongoing = `${pkg.priceString}/${getSubscriptionPeriodLabel(pkg.packageType)}`;
  const intro = formatIntroPriceOffer(pkg);
  return intro ? `${intro}, then ${ongoing}` : ongoing;
}

const PACKAGE_META: Record<string, { label: string; description: string; badge?: string }> = {
  MONTHLY: { label: 'Monthly', description: 'Cancel anytime' },
  ANNUAL: { label: 'Annual', description: '' },
};

function parsePriceAmount(priceString: string): number | null {
  const normalized = priceString.replace(/[^0-9.,]/g, '');
  if (!normalized) return null;

  const decimalNormalized =
    normalized.includes(',') && !normalized.includes('.')
      ? normalized.replace(',', '.')
      : normalized.replace(/,/g, '');

  const amount = Number.parseFloat(decimalNormalized);
  return Number.isFinite(amount) ? amount : null;
}

function getCurrencySymbol(priceString: string): string {
  const match = priceString.match(/[^\d.,\s]+/);
  return match?.[0] ?? '$';
}

function formatMonthlyPrice(symbol: string, amount: number): string {
  return `${symbol}${amount.toFixed(2)}`;
}

function formatWholeCurrency(symbol: string, amount: number): string {
  return `${symbol}${Math.round(amount).toLocaleString('en-US')}`;
}

export function getSubscriptionPackagePresentation(
  pkg: RCPackage,
  packages: RCPackage[],
): SubscriptionPackagePresentation {
  const meta = PACKAGE_META[pkg.packageType] ?? { label: pkg.packageType, description: '' };
  const amount = parsePriceAmount(pkg.priceString);
  const currencySymbol = getCurrencySymbol(pkg.priceString);
  const freeTrialDuration = formatFreeTrialDuration(pkg);
  const introOffer = formatIntroPriceOffer(pkg);

  if (pkg.packageType === 'MONTHLY') {
    if (introOffer) {
      return {
        ...meta,
        priceLabel: freeTrialDuration ? `${freeTrialDuration} free` : `${pkg.priceString}/mo`,
        comparisonLabel: `Then ${describePostTrialPricing(pkg)}`,
        description: freeTrialDuration ? 'Cancel anytime' : 'Introductory pricing',
      };
    }

    return {
      ...meta,
      priceLabel: freeTrialDuration ? `${freeTrialDuration} free` : `${pkg.priceString}/mo`,
      comparisonLabel: freeTrialDuration ? `Then ${pkg.priceString}/month` : null,
      description: 'Cancel anytime',
    };
  }

  if (pkg.packageType === 'ANNUAL' && amount !== null) {
    const monthlyPackage = packages.find((candidate) => candidate.packageType === 'MONTHLY');
    const monthlyAmount = parsePriceAmount(monthlyPackage?.priceString ?? '');
    const equivalentPerMonth = amount / 12;
    const annualSavings =
      monthlyAmount !== null ? Math.max(monthlyAmount * 12 - amount, 0) : 0;

    return {
      ...meta,
      priceLabel: freeTrialDuration
        ? `${freeTrialDuration} free`
        : `${formatMonthlyPrice(currencySymbol, equivalentPerMonth)}/mo`,
      comparisonLabel: freeTrialDuration
        ? `Then ${pkg.priceString}/year (${formatMonthlyPrice(currencySymbol, equivalentPerMonth)}/month)`
        : `${pkg.priceString}/year`,
      description: freeTrialDuration
        ? annualSavings > 0
          ? `Save ${formatWholeCurrency(currencySymbol, annualSavings)}/year`
          : 'Cancel anytime'
        : annualSavings > 0
        ? `Save ${formatWholeCurrency(currencySymbol, annualSavings)}/year`
        : meta.description,
      ...(annualSavings > 0 ? { badge: 'Best value' } : {}),
    };
  }

  return {
    ...meta,
    priceLabel: freeTrialDuration ? `${freeTrialDuration} free` : pkg.priceString,
    comparisonLabel: freeTrialDuration ? `Then ${describePostTrialPricing(pkg)}` : null,
    description: freeTrialDuration ? 'Cancel anytime' : meta.description,
  };
}
